import React from 'react'
import cx from 'classnames'
import { t } from 'ttag'
import { interweave } from 'helpers'
import styles from './Competencies.module.scss'
import introStyles from '../IntroPage/IntroPage.module.scss'
import reportStyles from '../../Report.module.scss'
import { TraitsMatch } from 'components/common/TraitsMatch/TraitsMatch'
import { RadarChart } from 'components/common/RadarChart/RadarChart'
import html2canvas from 'html2canvas'

const Competency = ({
  trait,
  traitScores,
  explanation,
  scoreLabel,
  language,
}) => {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{trait.label}</div>
        <div className={styles.chip}>
          <div className={styles.chipLabel}>{t`match`}:</div>
          <div className={styles.chipScore}>{scoreLabel}</div>
          <div className={styles.chipGraph}>
            <div className={cx(styles.low, styles.selected)} />
            <div
              className={cx(styles.medium, {
                [styles.selected]: trait.score > 1,
              })}
            />
            <div
              className={cx(styles.high, {
                [styles.selected]: trait.score > 2,
              })}
            />
          </div>
        </div>
      </div>
      {explanation.map((exp, index) => (
        <div key={index} className={styles.paragraph}>
          {exp
            .replace(new RegExp(t`a candidate`, 'gi'), t`someone`)
            .replace(new RegExp(t`the candidate`, 'gi'), t`someone`)}
        </div>
      ))}
      <TraitsMatch single data={traitScores} language={language} compact />
    </div>
  )
}

export const Competencies = ({
  data,
  radarData,
  getTraitScores,
  explanations,
  getScoreLabel,
  language,
}) => {
  const [chartImage, setChartImage] = React.useState(null)

  const showRadar = data.length > 2

  React.useEffect(() => {
    if (!showRadar) {
      setChartImage(null)
      return undefined
    }

    const captureChart = async () => {
      const chartElement = document.getElementById('chart-container')
      if (!chartElement) return

      const canvas = await html2canvas(chartElement)
      setChartImage(canvas.toDataURL('image/png'))
    }

    const timer = setTimeout(captureChart, 1000)
    return () => clearTimeout(timer)
  }, [showRadar, radarData])

  if (!data?.length) {
    return null
  }

  return (
    <div
      className={cx(reportStyles.containerWhite, styles.flowRoot)}
      id="report"
      data-testid="report"
    >
      <div className={cx(introStyles.header, introStyles.headerPadding)}>
        <div className={introStyles.name}>{t`Profile match`}</div>
        <div className={introStyles.position}>
          {t`Estimated match for each competency`}
        </div>
      </div>
      {showRadar ? (
        <>
          <div
            id="chart-container"
            className={styles.chartContainer}
            style={{
              display: chartImage ? 'none' : 'block',
            }}
          >
            <RadarChart
              data={radarData}
              single
              maxHeight={'15rem'}
              margin={{ top: 6, right: 20, bottom: 0, left: 20 }}
            />
          </div>
          {chartImage ? (
            <img id="chart-image" src={chartImage} alt="Chart Snapshot" />
          ) : null}
        </>
      ) : null}
      {interweave(
        data.map((competency, index) => (
          <Competency
            key={`${competency.label}-${index}`}
            trait={competency}
            traitScores={getTraitScores(index)}
            explanation={explanations[index]}
            scoreLabel={getScoreLabel(index)}
            language={language}
          />
        )),
        <div className={styles.divider} />,
      )}
    </div>
  )
}
